import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Users, Eye, Heart, Clock, TrendingUp, Activity, UserPlus, Headphones, Mic, BookOpen, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';
import DsiaAnalyticsPanel from '../components/DsiaAnalyticsPanel';

const fmtDuration = (mins) => {
  if (!mins) return '0 min';
  if (mins < 60) return `${mins.toFixed(0)} min`;
  const h = mins / 60;
  if (h < 24) return `${h.toFixed(1)} h`;
  const d = h / 24;
  return `${d.toFixed(1)} j`;
};

const Analytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    (async () => {
      try {
        const [a, u] = await Promise.all([
          axios.get('/api/admin/analytics'),
          axios.get('/api/admin/analytics/usage-time'),
        ]);
        setAnalytics(a.data);
        setUsage(u.data);
      } catch (e) {
        // ignore — page renders empty state
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  if (loading) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="flex items-center space-x-4 mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="analytics-back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Analytiques Détaillées
            </h1>
            <p className="text-gray-400 mt-1">Statistiques complètes de l&apos;application</p>
          </div>
        </div>

        {/* v49.7 — DS-IA analytics (super_admin + managers) */}
        <div className="mb-6"><DsiaAnalyticsPanel /></div>

        {/* Temps d'utilisation — la nouvelle star de la page */}
        {usage?.windows && (
          <section className="mb-8" data-testid="analytics-usage-time-section">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-cyan-400" />
              Temps d&apos;utilisation
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <UsageCard label="Aujourd&apos;hui (24h)" data={usage.windows.day} accent="from-cyan-500 to-blue-600" testid="usage-window-day" />
              <UsageCard label="7 derniers jours" data={usage.windows.week} accent="from-violet-500 to-fuchsia-600" testid="usage-window-week" />
              <UsageCard label="30 derniers jours" data={usage.windows.month} accent="from-emerald-500 to-teal-600" testid="usage-window-month" />
              <UsageCard label="12 derniers mois" data={usage.windows.year} accent="from-amber-500 to-orange-600" testid="usage-window-year" />
            </div>
          </section>
        )}

        {analytics && (
          <>
            {/* Croissance */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <UserPlus className="w-6 h-6 text-emerald-400" />
                Croissance utilisateurs
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiTile label="Utilisateurs totaux" value={analytics.users_total} color="from-blue-500 to-cyan-600" icon={<Users className="w-5 h-5" />} testid="kpi-users-total" />
                <KpiTile label="Nouveaux (24h)" value={analytics.new_users_24h} color="from-emerald-500 to-teal-600" icon={<UserPlus className="w-5 h-5" />} testid="kpi-new-24h" />
                <KpiTile label="Nouveaux (7j)" value={analytics.new_users_7d} color="from-violet-500 to-fuchsia-600" icon={<UserPlus className="w-5 h-5" />} testid="kpi-new-7d" />
                <KpiTile label="Nouveaux (30j)" value={analytics.new_users_30d} color="from-amber-500 to-orange-600" icon={<UserPlus className="w-5 h-5" />} testid="kpi-new-30d" />
              </div>
            </section>

            {/* Activité */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Activity className="w-6 h-6 text-magenta-400" />
                Activité
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiTile label="En ligne maintenant" value={analytics.online_now} color="from-green-500 to-emerald-600" icon={<Activity className="w-5 h-5" />} testid="kpi-online-now" />
                <KpiTile label="DAU (24h)" value={analytics.dau} color="from-cyan-500 to-blue-600" icon={<Eye className="w-5 h-5" />} testid="kpi-dau" />
                <KpiTile label="WAU (7j)" value={analytics.wau} color="from-violet-500 to-purple-600" icon={<Eye className="w-5 h-5" />} testid="kpi-wau" />
                <KpiTile label="MAU (30j)" value={analytics.mau} color="from-fuchsia-500 to-pink-600" icon={<Eye className="w-5 h-5" />} testid="kpi-mau" />
              </div>
            </section>

            {/* Engagement & contenus */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-yellow-400" />
                Engagement & contenus
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiTile label="Likes totaux" value={analytics.total_likes} color="from-red-500 to-pink-600" icon={<Heart className="w-5 h-5" />} testid="kpi-likes" />
                <KpiTile label="Gim Toli (24h)" value={analytics.posts_last_24h} color="from-magenta-500 to-pink-600" icon={<Mic className="w-5 h-5" />} testid="kpi-posts-24h" />
                <KpiTile label="Gim Toli (7j)" value={analytics.posts_last_7_days} color="from-violet-500 to-magenta-600" icon={<Mic className="w-5 h-5" />} testid="kpi-posts-7d" />
                <KpiTile label="Gim Toli (30j)" value={analytics.posts_last_30_days} color="from-fuchsia-500 to-purple-600" icon={<Mic className="w-5 h-5" />} testid="kpi-posts-30d" />
                <KpiTile label="Podcasts (30j)" value={analytics.podcasts_last_30_days} color="from-blue-500 to-indigo-600" icon={<Headphones className="w-5 h-5" />} testid="kpi-podcasts-30d" />
                <KpiTile label="Stories (30j)" value={analytics.stories_last_30_days} color="from-emerald-500 to-cyan-600" icon={<BookOpen className="w-5 h-5" />} testid="kpi-stories-30d" />
                <KpiTile label="Vues stories (30j)" value={analytics.story_views_last_30_days} color="from-cyan-500 to-sky-600" icon={<Eye className="w-5 h-5" />} testid="kpi-story-views-30d" />
                <KpiTile label="Créateurs actifs (30j)" value={analytics.active_users} color="from-amber-500 to-orange-600" icon={<Users className="w-5 h-5" />} testid="kpi-active-creators" />
              </div>
            </section>
          </>
        )}

        {/* Top utilisateurs par temps d'usage */}
        {usage?.top_users_30d?.length > 0 && (
          <section className="mb-8" data-testid="analytics-top-users">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-amber-400" />
              Top 10 — temps passé sur l&apos;app (30 j)
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden">
              {usage.top_users_30d.map((u, i) => (
                <div
                  key={u.user_id}
                  className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors"
                  data-testid={`top-user-${i}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm text-gray-400 w-6 text-right">{i + 1}.</span>
                    {u.profile_image_url ? (
                      <img src={getMediaUrl(u.profile_image_url)} alt={u.name} className="w-10 h-10 rounded-full object-cover bg-white/10" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-magenta-500 to-violet-600 flex items-center justify-center text-sm font-bold">
                        {(u.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-medium truncate">{u.name}</p>
                      <p className="text-xs text-gray-400 truncate">@{u.username}</p>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-cyan-300 whitespace-nowrap">{fmtDuration(u.total_minutes)}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

const KpiTile = ({ icon, label, value, color, testid }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={testid}>
    <div className={`w-10 h-10 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center mb-3`}>
      {icon}
    </div>
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <p className="text-2xl font-bold">{(value ?? 0).toLocaleString()}</p>
  </div>
);

const UsageCard = ({ label, data, accent, testid }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={testid}>
    <p className="text-xs text-gray-400 mb-2">{label}</p>
    <p className={`text-3xl font-bold bg-gradient-to-br ${accent} bg-clip-text text-transparent`}>{data.total_hours.toLocaleString()} h</p>
    <div className="mt-3 space-y-1 text-xs text-gray-300">
      <div className="flex justify-between"><span className="text-gray-500">Utilisateurs actifs</span><span className="font-semibold">{data.active_users.toLocaleString()}</span></div>
      <div className="flex justify-between"><span className="text-gray-500">Sessions</span><span className="font-semibold">{data.sessions.toLocaleString()}</span></div>
      <div className="flex justify-between"><span className="text-gray-500">Moy. / utilisateur</span><span className="font-semibold">{fmtDuration(data.avg_minutes_per_active_user)}</span></div>
      <div className="flex justify-between"><span className="text-gray-500">Durée / session</span><span className="font-semibold">{fmtDuration(data.avg_session_minutes)}</span></div>
    </div>
  </div>
);

export default Analytics;
